import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { json, redirect, type LoaderFunctionArgs } from '@remix-run/node';
import {
    useLoaderData,
    useLocation,
    useNavigate,
    useSearchParams,
} from '@remix-run/react';
import invariant from 'tiny-invariant';
import { prefetchCategoryImages } from '~/modules/category/util/prefetch-category-images';
import {
    getCanonicalHreflang,
    createMetaTitle,
    createMetaDescription,
    fullCountryNameFromCode,
    createOGTags,
    mergeMeta,
} from '~/modules/seo';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createProductsItemListSchema } from '~/modules/seo/schema/item-list-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { getDirectoryPageMeta } from '@modules/category/api';
import {
    pxToRem,
    useAppDisplayModeContext,
    useTheme,
} from '@archipro-design/aria';
import { PermissionEnum } from 'generated/graphql';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import {
    querySearchServiceAggregate,
    querySearchServiceSearch,
} from '~/modules/search';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import {
    createEmptyFilterStates,
    createFilterStates,
} from '~/modules/search/util/create-filter-states';
import { adRequest } from '@modules/adverts/api';
import AdvertBannerTile from '@modules/adverts/component/advert-banner-tile/AdvertBannerTile';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import {
    USER_PREFERENCE_KEY,
    useSearchFilters,
} from '~/modules/search/hook/use-search-filters';
import type { FilterStatesKey } from '~/modules/search/type/filter-state';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import type { AdvertCreative } from '@modules/adverts/type';
import { useTracker } from '@archipro-website/tracker';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { getSession } from '~/modules/root/util/session.server';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { hasFeature, type APHandle } from '~/modules/root';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProductCategories } from '~/modules/products-category/api';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { getString, parseURL } from '@archipro-website/top-navigation';
import { validateSort } from '~/modules/search/util/apply-es-search-sort';
import DetailedProductSpliter from '~/modules/products-category/component/DetailedProductSpliter';
import ArchizeenAdvertTile from '~/modules/tile/component/archizeen-tile/ArchizeenAdvertTile';
import { getArticleTileViewModelFromAd } from '~/modules/adverts/utils/map-ad-data-to-tile.server';
import type { ArticleTileViewModel } from '~/modules/article';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { noIndexProductCategory } from '~/modules/seo/util/no-index-product-category';
import { isFirstPage } from '~/modules/search/util/search-param-helper';
import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import { getPermissions } from '~/modules/user';
import {
    convertToProductSuppliersUrl,
    isProductSuppliersPage,
} from '~/modules/url';
import findLastIndex from 'lodash.findlastindex';
import { getUrlHasFilters } from '~/modules/search/util/get-url-has-filters.server';
import {
    DIRECTORY_IMAGE_WIDTH_DESKTOP_QUARTER,
    DIRECTORY_IMAGE_WIDTH_MOBILE_QUARTER,
} from '@modules/root/util/get-directory-tile-image-props';
import BackToTopButton from '@modules/directory/component/BackToTopButton';
import { getProductsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-product';
import type { ProductTileViewModel as ProductTileViewModelRocco } from '@rocco/ui/tile-product/view-models/product';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { SearchIndexes } from '~/modules-rocco/search/types/enum/search-indexes';
import { CategoryPageSearchIsland } from '~/ui-rocco/pages/category-page/CategoryPageSearchIsland';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { mapProductToTileViewModel } from '~/modules-rocco/products/data-mappers/tile-product';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { getProfessionalsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-professional';
import { CheckboxBlankLine, CheckboxFill } from '@archipro-design/icons';
import { buildPaginationUrl } from '@modules-rocco/pagination/util/build-pagination-url';
import { Button as RoccoButton } from '@rocco/components/button';
import FilterBarFilters from '~/modules/search/components/search-filter-section/FilterBarFilters';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useOpenLogin } from '~/utils-rocco/hooks/open-login-modal';
import { useUser } from '~/modules/user/hook/use-user';
import { VirtualizedGrid } from '~/components/VirtualizedGrid';
import { cn } from '@rocco/utils/cn';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { getItemToAmplify } from '~/modules/professional/api/get-item-to-amplify.server';
import { AnalyticsAmplifierTile } from '@rocco/ui/tile-analytics-amplifier/components/AnlyticsAmplifier';
import { useABTestIsOn } from '~/modules/root/hook/use-growthbook';
import { useWindowSize } from '~/modules/root/hook/useWindowSize';
import { getHiddenFacets } from '~/modules/product/api/get-facets.server';
import { PageBottomSection } from '~/ui-rocco/pages/shared/PageBottomSection';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches, location } = props;
        const { rootData, appData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.directoryPageMeta?.category
                    ? `Buy ${data?.directoryPageMeta?.category} Online`
                    : 'Page Not Found',
            ],
            true,
            data?.pagination?.page
        );

        invariant(
            rootData?.localisation.country,
            'rootData is required for product category SEO'
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            ` Browse a wide range of ${
                data?.directoryPageMeta?.category
            } on ArchiPro. We offer high-quality products from leading brands in ${fullCountryNameFromCode(
                rootData.localisation.country
            )}. Shop now!`,
        ]);

        const productsListSchema = createProductsItemListSchema(props);
        const FAQSchema = createFAQSchema(data?.localisation?.FAQSchemas ?? []);
        const ldSchema = createLDSchema([productsListSchema, FAQSchema]);

        const numberTilesToPreload = data?.appDisplayMode === 'desktop' ? 4 : 2;
        const preloadImageLinks = data?.searchResult?.hits
            ? prefetchCategoryImages(
                  data.searchResult.hits,
                  numberTilesToPreload,
                  getImagesSizes({
                      desktop: DIRECTORY_IMAGE_WIDTH_DESKTOP_QUARTER,
                      mobile: DIRECTORY_IMAGE_WIDTH_MOBILE_QUARTER,
                  }),
                  data?.appDisplayMode === 'mobile'
              )
            : [];

        const metaDescriptors = [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
                type: 'website',
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImageLinks,
        ];

        return noIndexProductCategory(
            metaDescriptors,
            location.pathname + location.search,
            data?.productCategory?.customSeoOnFilters,
            appData?.user?.FeatureFlags
        );
    }
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
};

const CATEGORY_IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    'libraryRanges',
    'awards',
];

const DEFAULT_PRODUCT_PAGE_SIZE = 40;
const DEFAULT_SUPPLIERS_PAGE_SIZE = 39;
const DEFAULT_SORT = 'featured';
export const PRODUCT_TILE_EXPANDED_WIDTH = 368;
export const PRODUCT_TILE_COLLAPSED_WIDTH = 340;
export const PRODUCT_TILE_PAGING_WIDTH = 175;
export const PRODUCT_TILE_BULLET_SIZE = 11;

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { appDisplayMode, config, user, featureFlags } = context;

    const originalUrl = new URL(request.url);
    const isSuppliersEndpoint = isProductSuppliersPage(originalUrl.pathname);
    const url = isSuppliersEndpoint
        ? new URL(request.url.replace('/suppliers', ''))
        : originalUrl;
    const searchParams = url.searchParams;

    const session = await getSession(request);
    const isDesktop = appDisplayMode === 'desktop';

    const { basePath, params } = parseURL(url.pathname + url.search);
    const urlSort = validateSort(getString(params, 'sort'));
    const hasFilters = getUrlHasFilters(url);

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${searchParams.toString()}`,
            'product',
            'directory',
            { pageSize },
            DEFAULT_SORT
        );

        const updateUserPreference =
            searchParams.get(USER_PREFERENCE_KEY) === '1';

        if (updateUserPreference) {
            session.setUserPreferences({
                forSale: queryParams.filters?.isPurchasable ?? false,
            });
            searchParams.delete(USER_PREFERENCE_KEY);
            const redirectUrl = `${url.pathname}?${searchParams.toString()}`;
            throw redirect(
                isSuppliersEndpoint
                    ? convertToProductSuppliersUrl(redirectUrl)
                    : redirectUrl,
                {
                    headers: await session.getHeaders(),
                }
            );
        }

        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });

        let searchFilters = queryParams.filters;

        if (isSuppliersEndpoint) {
            // To get product suppliers:
            // 1. Get all the professionals by aggregating products by professionals.
            // 2. Search professionals filtered by the professionals from the first step.
            const professionalAggregations = await querySearchServiceAggregate({
                context,
                queryParams: {
                    ...aggregationProps,
                    aggregations: {
                        aggProfessionals: {
                            search: '',
                            size: 1000,
                        },
                    },
                },
            });
            if (professionalAggregations?.professionals) {
                const professionals = professionalAggregations.professionals;
                // here we need to swap data in current queryParams to be able to fetch professionals
                queryParams.searchType = 'professional';
                // we need to keep queryParams.filters as is, so the page could show the correct filters
                searchFilters = {
                    professionals: professionals.items.map((i) => i.URLSegment),
                };
            }
        }

        return Promise.all([
            queryParams,
            aggregationProps,
            querySearchServiceSearch({
                context,
                queryParams: {
                    ...queryParams,
                    filters: searchFilters,
                },
            }),
            querySearchServiceAggregate({
                context,
                queryParams: aggregationProps,
            }),
        ]);
    };

    const promotedAdsPromise = adRequest({
        type: 'PromotedDirectoryPlacement',
        requestUrl: request.url,
        addPathContext: ['region'],
        ...context,
        request,
    });

    let [
        directoryPageMeta,
        // rootDirectoryPageMeta will be used as a fallback for missing category descriptions
        rootDirectoryPageMeta,
        productCategory,
        recentlyViewedProductsResult,
        ads,
        localisation,
        promotedAds,
        hiddenFacets,
        esResults,
        activeDirectoryCategoriesCount,
        amplifiedItems,
    ] = await Promise.all([
        getDirectoryPageMeta({
            ...context,
            url,
        }),
        // fetch the root products category meta to use its description as a fallback
        getDirectoryPageMeta({
            url: new URL('/products', request.url),
            ...args.context,
        }),
        getProductCategories({
            ...context,
            args,
            config,
        }),
        getRecentlyViewedServer({ type: 'Products' }, context),
        adRequest({
            type: 'CategoryPlacement',
            requestUrl: request.url,
            addPathContext: ['region'],
            ...context,
            request,
        }),
        getPageLocalisation(args),
        promotedAdsPromise,
        getHiddenFacets({ context, user }),
        // Waterfall call to query Promoted Ads and products.
        // We need to wait for Promoted Ads to be able to determine if we need to fetch one less product.
        (async () => {
            const promotedAdsResult = await promotedAdsPromise;
            const pageSize = isSuppliersEndpoint
                ? DEFAULT_SUPPLIERS_PAGE_SIZE
                : DEFAULT_PRODUCT_PAGE_SIZE;

            return queryES(
                isDesktop &&
                    isFirstPage(searchParams) &&
                    promotedAdsResult &&
                    !hasFilters &&
                    promotedAdsResult.length > 0 // promoted article just for desktop atm. might add mobile later.
                    ? pageSize - 1
                    : pageSize
            );
        })(),
        getActiveDirectoryCategoriesCount({}, context),
        getItemToAmplify({
            context,
            link: basePath,
            limit: isDesktop ? 5 : 4,
        }),
    ]);

    const [queryParams, aggregationProps, searchResult, aggregations] =
        esResults;

    const hasMarketingView = getPermissions(
        user,
        PermissionEnum.BmMarketingView
    );
    const facetFilterFeatureEnabled = hasFeature(featureFlags, 'filter_facet');

    const searchParamsForSale = queryParams.filters?.isPurchasable ?? false;
    // block forSale preference for users without shopnav feature
    const userPreferenceForSale =
        session.getUserPreferences()?.forSale ?? false;

    if (!isSuppliersEndpoint && !searchParamsForSale && userPreferenceForSale) {
        // if we have forSale items, forSale filter not applied and user prefer to see forSale items, add the forSale filter
        searchParams.set('forSale', '1');
        throw redirect(`${url.pathname}?${searchParams.toString()}`);
    }

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: isSuppliersEndpoint
            ? DEFAULT_SUPPLIERS_PAGE_SIZE
            : DEFAULT_PRODUCT_PAGE_SIZE,
    });

    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps.aggregations,
              searchType: 'product',
              visibilityType: 'directory',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: urlSort,
              facetFilterFeatureEnabled,
              hasMarketingView,
              hiddenFacetIds: hiddenFacets.map((facet) => facet.ID),
          })
        : createEmptyFilterStates({ visibilityType: 'directory' });

    const searchIslandPlaceholders = mapCategoryCountToPlaceholder(
        activeDirectoryCategoriesCount?.products ?? [],
        `Search products`,
        'products'
    );

    const appliedFilterCount = getAppliedFilterNumber(filterStates, [
        'searchLocation',
    ]);

    let productTilesRocco: TileViewModel<ProductTileViewModelRocco>[] = [];
    let professionalTilesRocco: TileViewModel<ProfessionalTileViewModel>[] = [];
    let adsShuffled: AdvertCreative[] = [];
    let promotedAdTiles: ArticleTileViewModel[] = [];
    let enableAwardBadges = false;

    if (isSuppliersEndpoint) {
        const professionals = getProfessionalsFromESSearchResults(searchResult);
        professionalTilesRocco = professionals.map((professional) => ({
            aspectRatio: 'default',
            model: professional,
        }));
    } else {
        const products = getProductsFromESSearchResults(searchResult, null);
        productTilesRocco = products.map((product) => ({
            aspectRatio: 'square',
            model: product,
        }));

        adsShuffled = ads ? shuffleArray(ads) : [];

        promotedAdTiles =
            shuffleArray(promotedAds)?.reduce((acc, ad) => {
                const tile = getArticleTileViewModelFromAd(ad);
                if (tile) {
                    acc.push(tile);
                }
                return acc;
            }, [] as ArticleTileViewModel[]) ?? [];

        enableAwardBadges = hasFeature(featureFlags, 'award_badges');
    }

    return json(
        {
            isSuppliersEndpoint,
            appDisplayMode,
            directoryPageMeta,
            searchResult,
            productCategory,
            productTilesRocco,
            professionalTilesRocco,
            pagination,
            recentlyViewedProducts:
                recentlyViewedProductsResult?.RecentlyViewedProducts.map(
                    (product) =>
                        mapProductToTileViewModel(product, user, {
                            appDisplayMode,
                        })
                ) ?? [],
            // If directory doesn't have a description, use the root category description as a fallback
            categoryDescription:
                directoryPageMeta?.directoryHeaderDescription ||
                rootDirectoryPageMeta?.directoryHeaderDescription ||
                "Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from New Zealand's leading product suppliers.",
            ads: adsShuffled,
            promotedAds: promotedAdTiles,
            localisation,
            filterStates: isSuppliersEndpoint
                ? {
                      ...filterStates,
                      totalCount: searchResult?.nbHits ?? 0,
                  }
                : filterStates,
            appliedFilterCount,
            enableAwardBadges,
            searchIslandPlaceholders,
            amplifiedItems,
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

const ProductsCategoryPage = () => {
    const appDisplayMode = useAppDisplayModeContext();
    const { setCategory } = useCurrentCategory();
    const user = useUser();
    const { desktop } = appDisplayMode;
    const {
        isSuppliersEndpoint,
        categoryDescription,
        directoryPageMeta,
        recentlyViewedProducts,
        filterStates,
        productTilesRocco,
        professionalTilesRocco,
        searchIslandPlaceholders,
    } = useLoaderData<typeof loader>();

    const tracker = useTracker();
    const [localIsSuppliers, setLocalIsSuppliers] =
        useState(isSuppliersEndpoint);
    const [disableHighlightTransition, setDisableHighlightTransition] =
        useState(false);

    useEffect(() => {
        setLocalIsSuppliers(isSuppliersEndpoint);
    }, [isSuppliersEndpoint]);

    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const filtersRef = useRef<HTMLDivElement | null>(null);

    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedCompanies = searchParams.getAll('professionals').join(' | ');

    useEffect(() => {
        tracker.log('productListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                brand: selectedCompanies,
            },
        });
    }, [selectedCategory, selectedCompanies, tracker]);

    useEffect(() => {
        setCategory('Products');
        return () => setCategory(null);
    }, [setCategory]);

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    const gridColumns = desktop ? (filterDrawerOpen ? 4 : 5) : 2;

    const searchFilters = useSearchFilters({
        category: 'product',
        initialStates: filterStates,
        isCategoryPage: true,
        ignoreFilterKeys: CATEGORY_IGNORE_FILTER_KEYS,
    });

    const searchFiltersSuppliers = useMemo(() => {
        return isSuppliersEndpoint
            ? {
                  ...searchFilters,
                  filterBarFilters: [],
                  filterStates: {
                      queryFilters: searchFilters.filterStates.queryFilters,
                      visibilityType: searchFilters.filterStates.visibilityType,
                      sortBy: searchFilters.filterStates.sortBy,
                      totalCount: searchFilters.filterStates.totalCount,
                      category: searchFilters.filterStates?.category,
                  },
              }
            : searchFilters;
    }, [isSuppliersEndpoint, searchFilters]);

    const handleSuppliersClick = useCallback(() => {
        // Reset all filters when switching between suppliers and products.
        navigate(isSuppliersEndpoint ? '/products' : '/products/suppliers');
    }, [isSuppliersEndpoint, navigate]);

    const hasProducts = filterStates.totalCount > 0;

    // Determine which grid to show based on data availability
    const shouldShowSupplierGrid =
        isSuppliersEndpoint && professionalTilesRocco.length > 0;
    const shouldShowProductGrid =
        !isSuppliersEndpoint && productTilesRocco.length > 0;

    const openLogin = useOpenLogin();

    // Check if the UI updates feature flag is enabled
    const uiUpdatesJune2025Enabled = hasFeature(
        user.FeatureFlags,
        'UI_updates_june_2025'
    );

    return (
        <div className="mx-auto w-full qhd:max-w-screen-qhd">
            {desktop ? (
                <TitleSectionDesktop
                    category={directoryPageMeta.directoryHeaderTitle}
                    rootCategory={
                        isSuppliersEndpoint ? 'Suppliers.' : 'Products.'
                    }
                    categoryDescription={categoryDescription}
                    itemsInCategory={filterStates.totalCount}
                    isRoot={directoryPageMeta.isRoot}
                />
            ) : (
                <TitleSectionMobile
                    title={directoryPageMeta.directoryHeaderTitle}
                    categoryDescription={categoryDescription}
                    subtitle={''}
                >
                    <TitleSectionFilterMobile
                        esCategory="product"
                        searchFilters={searchFiltersSuppliers}
                        isSuppliersPage={isSuppliersEndpoint}
                        quickFiltersBefore={
                            hasProducts
                                ? [
                                      {
                                          key: `suppliers-tab`,
                                          iconBefore: isSuppliersEndpoint ? (
                                              <CheckboxFill />
                                          ) : (
                                              <CheckboxBlankLine />
                                          ),
                                          children: 'Suppliers',
                                          onClick: handleSuppliersClick,
                                          selected: false,
                                          transparent: true,
                                          outlined: true,
                                          disabled: false,
                                          className: '',
                                      },
                                  ]
                                : []
                        }
                    />
                </TitleSectionMobile>
            )}

            {desktop && (
                <CategoryFilterSection
                    category={isSuppliersEndpoint ? 'professional' : 'product'}
                    filterDrawerOpen={filterDrawerOpen}
                    searchFilters={searchFiltersSuppliers}
                    breadcrumbs={
                        isSuppliersEndpoint
                            ? directoryPageMeta.breadcrumbs.map(
                                  ({ href, label }) => ({
                                      href: convertToProductSuppliersUrl(href),
                                      label,
                                  })
                              )
                            : directoryPageMeta.breadcrumbs
                    }
                    componentBefore={
                        <div
                            ref={filtersRef}
                            className="flex items-center gap-3"
                        >
                            {/* Hide Filters (toggle) should be first on the left */}
                            <CollapsibleToggle
                                filterDrawerOpen={filterDrawerOpen}
                                toggleFilterDrawer={toggleFilterDrawer}
                            />

                            {/* Render the other filter buttons on the left */}
                            <FilterBarFilters
                                filters={
                                    searchFiltersSuppliers.filterBarFilters ??
                                    []
                                }
                                updateFilter={
                                    searchFiltersSuppliers.updateFilter
                                }
                                componentBarAddon={null}
                                filterStates={
                                    searchFiltersSuppliers.filterStates
                                }
                            />
                        </div>
                    }
                    componentBarAddon={
                        hasProducts ? (
                            <div className="flex items-center gap-3">
                                <div className="relative flex items-center gap-2 rounded-full bg-[#ECECEC] px-[0.45rem] py-[0.3rem]">
                                    {/* Sliding highlight background for the active tab on the toggle slider*/}
                                    <div
                                        aria-hidden
                                        className={cn(
                                            'absolute left-[0.45rem] top-[0.3rem] h-[calc(100%-0.6rem)] rounded-full bg-white shadow-md duration-300',
                                            disableHighlightTransition
                                                ? ''
                                                : 'transition-transform',
                                            localIsSuppliers
                                                ? 'translate-x-full'
                                                : 'translate-x-0'
                                        )}
                                        style={{ width: 'calc(50% - 0.45rem)' }}
                                    />

                                    <RoccoButton
                                        weight="normal"
                                        clickAnimation="scale"
                                        onClick={() => {
                                            if (isSuppliersEndpoint) {
                                                // Now we animate the transition first, then navigate to the toggled page after.
                                                setDisableHighlightTransition(
                                                    true
                                                );
                                                setLocalIsSuppliers(false);

                                                requestAnimationFrame(() => {
                                                    setTimeout(() => {
                                                        handleSuppliersClick();
                                                        setDisableHighlightTransition(
                                                            false
                                                        );
                                                    }, 50);
                                                });
                                            }
                                        }}
                                        className={cn(
                                            'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                            !isSuppliersEndpoint
                                                ? 'font-medium text-[#232323]'
                                                : 'font-normal text-[#232323] opacity-60'
                                        )}
                                    >
                                        <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                            Products
                                        </span>
                                    </RoccoButton>

                                    <RoccoButton
                                        weight="normal"
                                        clickAnimation="scale"
                                        onClick={() => {
                                            if (!isSuppliersEndpoint) {
                                                // Now we animate the transition first, then navigate to the toggled page after.
                                                setDisableHighlightTransition(
                                                    true
                                                );
                                                setLocalIsSuppliers(true);
                                                requestAnimationFrame(() => {
                                                    setTimeout(() => {
                                                        handleSuppliersClick();
                                                        setDisableHighlightTransition(
                                                            false
                                                        );
                                                    }, 50);
                                                });
                                            }
                                        }}
                                        className={cn(
                                            'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                            isSuppliersEndpoint
                                                ? 'font-medium text-[#232323]'
                                                : 'font-normal text-[#232323] opacity-60'
                                        )}
                                    >
                                        <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                            Brands
                                        </span>
                                    </RoccoButton>
                                </div>
                            </div>
                        ) : null
                    }
                />
            )}

            <CollapsiblePageLayout
                sidePanel={
                    desktop ? (
                        <CategoryFilterNav
                            category={
                                isSuppliersEndpoint ? 'professional' : 'product'
                            }
                            searchFilters={searchFiltersSuppliers}
                            hideCategorySectionTitle={true}
                            hideCategoryBackButton={true}
                        />
                    ) : null
                }
                hideSidePanel={!filterDrawerOpen}
            >
                <div>
                    {desktop &&
                        !!searchFiltersSuppliers.appliedFilters?.length && (
                            <AppliedFilters
                                filterItems={
                                    searchFiltersSuppliers.appliedFilters
                                }
                                resetFilters={() =>
                                    searchFiltersSuppliers.resetFilters(
                                        resetFilterStates(
                                            searchFiltersSuppliers.filterStates,
                                            [
                                                ...CATEGORY_IGNORE_FILTER_KEYS,
                                                'category',
                                            ]
                                        )
                                    )
                                }
                            />
                        )}

                    {shouldShowSupplierGrid ? (
                        <SupplierGrid filterDrawerOpen={filterDrawerOpen} />
                    ) : shouldShowProductGrid ? (
                        <ProductGrid
                            gridColumns={gridColumns}
                            filterDrawerOpen={filterDrawerOpen}
                            isPurchasableChecked={
                                searchFiltersSuppliers.filterStates
                                    .isPurchasable?.checked ?? false
                            }
                        />
                    ) : (
                        <NoResultsSection
                            resultsType={
                                isSuppliersEndpoint ? 'suppliers' : 'products'
                            }
                        />
                    )}
                </div>
            </CollapsiblePageLayout>

            <div className="my-10 flex flex-col gap-10 md:my-[12.8125rem] md:gap-[13.75rem]">
                <CategoryPageRecentlyViewed
                    displayMode={appDisplayMode.desktop ? 'desktop' : 'mobile'}
                    items={recentlyViewedProducts}
                />

                <CategoryPageFAQ
                    faqSchemas={directoryPageMeta.faqSchemas}
                    footerContent={directoryPageMeta.footerContent}
                />

                {desktop && <PageBottomSection openLogin={openLogin} />}
            </div>

            {!uiUpdatesJune2025Enabled && (
                <CategoryPageSearchIsland
                    searchIndex={SearchIndexes.Products}
                    displayMode={appDisplayMode.desktop ? 'desktop' : 'mobile'}
                    placeholders={searchIslandPlaceholders}
                    scrollThreshold={0}
                    forceHide={filterDrawerOpen}
                />
            )}

            <BackToTopButton />
        </div>
    );
};

export default ProductsCategoryPage;

// ProductGrid component for products
interface ProductGridProps {
    filterDrawerOpen: boolean;
    gridColumns: 1 | 2 | 3 | 4 | 5;
    isPurchasableChecked: boolean;
}

const ProductGrid = React.memo(
    ({
        filterDrawerOpen,
        gridColumns,
        isPurchasableChecked,
    }: ProductGridProps) => {
        const gridRef = useRef<HTMLDivElement | null>(null);
        useCategoryTilesTracking(gridRef);

        const {
            pagination,
            productTilesRocco: productTiles,
            directoryPageMeta,
            ads,
            promotedAds,
            appliedFilterCount,
            amplifiedItems: amplifiedItemsLoader,
        } = useLoaderData<typeof loader>();

        const [amplifiedItems, setAmplifiedItems] =
            useState(amplifiedItemsLoader);
        const remixLocation = useLocation();
        const { desktop, mobile } = useAppDisplayModeContext();
        const theme = useTheme();
        const { toggleFilterDrawer } = useFilterDrawerPreference();
        const { handleFollow, isInDesignBoard } = useHandleFollow();
        const { isSafari } = useDeviceBrowserContext();
        const productTilesRocco = useTilesAdminData(
            productTiles as TileViewModel<ProductTileViewModelRocco>[]
        );

        // Keep the first value returned by loader to avoid changing when loader revalidate
        const adsMemoized = useLocationMemo(ads, ['region']);
        const promotedAdsMemoized = useLocationMemo(promotedAds, ['region']);
        const isAnalyticsAmplifierTilesOn = useABTestIsOn(
            'analytics-amplifier-tiles'
        );

        // Process product tiles
        const productBaseTiles = useMemo(() => {
            return productTilesRocco.map((tile) => {
                return (
                    <div
                        key={tile.model.id}
                        className="transform-gpu will-change-transform"
                        style={{
                            transition: 'transform 300ms ease',
                        }}
                    >
                        <CategoryPageTileWithAdminActions tile={tile} />
                    </div>
                );
            });
        }, [productTilesRocco]);

        const { width: windowWidth } = useWindowSize();
        const isLargeScreen = windowWidth && windowWidth > 1920;

        // Process all tiles including ads and promoted content
        const processedTiles = useMemo(() => {
            const result = [...productBaseTiles];

            // Add amplified item tiles with positions based on filter drawer state
            const tilePositions =
                mobile || (filterDrawerOpen && !isLargeScreen)
                    ? [7, 14, 21, 28] // When filter drawer is open and screen is not large
                    : [9, 18, 27, 36, 40]; // When filter drawer is closed or screen is large (>1900px)

            if (isAnalyticsAmplifierTilesOn) {
                if (amplifiedItems?.length) {
                    tilePositions.forEach((position, index) => {
                        const item = amplifiedItems[index];
                        if (position < result.length && item) {
                            const amplifiedTile = (
                                <div
                                    key={`amplified_${position}`}
                                    className="aspect-[6/8] md:aspect-[6/7]"
                                >
                                    <AnalyticsAmplifierTile
                                        professionalId={item.professionalId}
                                        image={item.image}
                                        logo={item.logo}
                                        name={item.title}
                                        subtitle={item.aiServiceSummary}
                                        website={item.websiteLink}
                                        backgroundColor={item.backgroundColor}
                                    />
                                </div>
                            );
                            result.splice(position, 0, amplifiedTile);
                        }
                    });
                }
            }

            // Insert promoted ad tile if conditions are met
            if (
                desktop &&
                pagination.page === 1 &&
                result.length > 0 &&
                promotedAdsMemoized.length &&
                promotedAdsMemoized[0] &&
                promotedAdsMemoized[0].ad
            ) {
                const offset = filterDrawerOpen ? 7 : 9;
                if (result.length >= offset) {
                    const adTile = promotedAdsMemoized[0];
                    const professionalID = adTile.professionalID ?? 0;
                    const imageProps = {
                        src: adTile.image,
                        width: '100%',
                        height: '100%',
                        sizes: getImagesSizes(310),
                        alt: adTile.title,
                    };
                    result.splice(
                        offset,
                        0,
                        <ArchizeenAdvertTile
                            tag={'ARTICLE'}
                            advertCreative={promotedAdsMemoized[0].ad}
                            key={`promoted_${adTile.ID}`}
                            prefetch="intent"
                            image={imageProps}
                            size={!filterDrawerOpen ? 'large' : 'medium'}
                            variant={'08'}
                            variables={{
                                color: theme.siteVariables.colors.charcoal[
                                    '250'
                                ],
                                dynamicSizingWidth: 310,
                                dynamicSizingGapWidth: 0,
                                tilePadding: pxToRem(10),
                            }}
                            onFavourite={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleFollow(
                                    { id: professionalID },
                                    'ProfessionalPin'
                                );
                            }}
                            isFavourited={isInDesignBoard(
                                professionalID,
                                'ProfessionalPin'
                            )}
                            title={adTile.title}
                            label={{
                                children: adTile.author,
                                variant: '06',
                            }}
                            date={{
                                children: adTile.date,
                                variant: '06',
                            }}
                            readTime={{
                                children: adTile.readTime,
                                variant: '06',
                            }}
                        />
                    );
                }
            }

            // Add DetailedProductSpliter if needed
            const category =
                directoryPageMeta.category !== 'Products'
                    ? directoryPageMeta.category
                    : undefined;
            if (productTilesRocco && isPurchasableChecked) {
                const lastPurchasableItemIndex = findLastIndex(
                    productTilesRocco,
                    (t) => t.model.canPurchase
                );
                const purchasableCount =
                    productTilesRocco?.filter((t) => t.model.canPurchase)
                        .length ?? 0;
                if (
                    purchasableCount === 0 ||
                    (lastPurchasableItemIndex !== undefined &&
                        lastPurchasableItemIndex !== -1)
                ) {
                    const rowIndex = Math.ceil(
                        (lastPurchasableItemIndex + 1) / gridColumns
                    );
                    const insertionIndex = rowIndex * gridColumns;
                    if (insertionIndex < productTilesRocco.length) {
                        const moreProductsComing = (
                            <DetailedProductSpliter
                                key="MoreProductsComing"
                                category={category}
                                columns={gridColumns}
                            />
                        );
                        result.splice(insertionIndex, 0, moreProductsComing);
                    }
                }
            }

            // Add banner ad if no filters are applied
            if (
                appliedFilterCount === 0 &&
                adsMemoized.length &&
                adsMemoized[0]
            ) {
                result.unshift(
                    <AdvertBannerTile
                        key="AdvertBannerTile"
                        advertCreative={adsMemoized[0]}
                        columns={gridColumns}
                        desktop={desktop}
                    />
                );
            }

            return result;
        }, [
            productBaseTiles,
            mobile,
            desktop,
            pagination.page,
            promotedAdsMemoized,
            filterDrawerOpen,
            isLargeScreen,
            theme,
            handleFollow,
            isInDesignBoard,
            directoryPageMeta.category,
            isPurchasableChecked,
            productTilesRocco,
            gridColumns,
            appliedFilterCount,
            adsMemoized,
            amplifiedItems,
            isAnalyticsAmplifierTilesOn,
        ]);

        useEffect(() => {
            // Randomise amplified items when URL changes, but not when forms are submitted.
            setAmplifiedItems(amplifiedItemsLoader);
        }, [amplifiedItemsLoader, remixLocation.key]);

        if (productTilesRocco.length === 0) {
            return <NoResultsSection resultsType={'products'} />;
        }

        return (
            <>
                <VirtualizedGrid
                    items={processedTiles}
                    renderItem={(item) => item}
                    startMarker={
                        <div className="hidden">
                            <CollapsibleToggle
                                filterDrawerOpen={filterDrawerOpen}
                                toggleFilterDrawer={toggleFilterDrawer}
                            />
                        </div>
                    }
                    endMarker={
                        pagination.totalPages > 1 && (
                            <div className="pt-[6.5625rem] md:pt-20">
                                <TilePagination
                                    currentPath={remixLocation}
                                    current={pagination.page}
                                    total={pagination.totalPages}
                                    urlBuilder={buildPaginationUrl}
                                    visibleCount={desktop ? 4 : 3}
                                />
                            </div>
                        )
                    }
                    className={cn(
                        'grid transition-all duration-300 ease-in-out',
                        filterDrawerOpen
                            ? 'grid-cols-2 md:grid-cols-4 qhd:grid-cols-5'
                            : 'grid-cols-2 md:grid-cols-5',
                        'gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20'
                    )}
                    enabled={isSafari}
                    initialVisibleCount={15}
                    batchSize={10}
                    loadMoreThreshold={0.7}
                    reduceThreshold={0.3}
                    minVisibleCount={15}
                />
            </>
        );
    }
);

// SupplierGrid component for suppliers
interface SupplierGridProps {
    filterDrawerOpen: boolean;
}

const SupplierGrid = React.memo(({ filterDrawerOpen }: SupplierGridProps) => {
    const { pagination, professionalTilesRocco: professionalTiles } =
        useLoaderData<typeof loader>();

    const remixLocation = useLocation();
    const appDisplayMode = useAppDisplayModeContext();
    const professionalTilesRocco = useTilesAdminData(
        professionalTiles as TileViewModel<ProfessionalTileViewModel>[]
    );

    // Process supplier tiles
    const supplierBaseTiles = useMemo(() => {
        return professionalTilesRocco.map((tile) => {
            return (
                <div
                    key={tile.model.id}
                    className="transform-gpu will-change-transform"
                    style={{
                        transition: 'transform 300ms ease',
                    }}
                >
                    <CategoryPageTileWithAdminActions tile={tile} />
                </div>
            );
        });
    }, [professionalTilesRocco]);

    if (supplierBaseTiles.length === 0) {
        return <NoResultsSection resultsType={'suppliers'} />;
    }

    return (
        <>
            <Grid
                colClasses={
                    filterDrawerOpen
                        ? 'grid-cols-1 md:grid-cols-3 qhd:grid-cols-4'
                        : 'grid-cols-1 md:grid-cols-4'
                }
                rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                className="transition-all duration-300 ease-in-out"
            >
                {supplierBaseTiles}
            </Grid>

            {pagination.totalPages > 1 && (
                <div className="pt-[6.5625rem] md:pt-20">
                    <TilePagination
                        currentPath={remixLocation}
                        current={pagination.page}
                        total={pagination.totalPages}
                        urlBuilder={buildPaginationUrl}
                        visibleCount={appDisplayMode.desktop ? 4 : 3}
                    />
                </div>
            )}
        </>
    );
});
